import React, {  useState } from "react";
import "../../../../../../css/CashflowModeller2/Cards/Income/IncomeCardSmall.css";
import "../../../../../../css/CashflowModeller2/Cards/Cards.css";
import AddEditButton from "../../Buttons/AddEditButton";
import { useCfmContext } from "../../../Context/CfmContext";
import { useContextUpdater } from "../../../Hooks/UseContextUpdater"; // <-- import the hook
import IncomeDialogBox from "../../DialogBoxes/IncomeDialogBox";
import TimelineOptions from "../../../Utils/TimelineOptions";
import ArrowDown from "../../Buttons/ArrowDown";
import Input1Big from "../../Inputs/Input1Big";

/**
 * IncomeCardSmall is a React functional component that displays a compact card view of income sources.
 * 
 * Features:
 * - Shows a list of income items from the context, or a default state pension income if none exist.
 * - Allows toggling the card's collapsed/expanded state.
 * - Provides an Add/Edit button to open a dialog for modifying income entries (disabled if no details exist).
 * - Displays each income's type, start age, and amount (with currency and unit options).
 * - Input fields are read-only and can be disabled based on context state.
 * 
 * Context:
 * - Uses `useCfmContext` to access the current state.
 * - Uses `useContextUpdater("income")` to update, add, or delete income items in the context.
 * 
 * State:
 * - `isDialogOpen`: Controls visibility of the income dialog box.
 * - `isCollapsed`: Controls whether the card is collapsed or expanded.
 * 
 * @component
 * @returns {JSX.Element} The rendered IncomeCardSmall component.
 */
const IncomeCardSmall = () => {
  const { state } = useCfmContext();
  const income =
    state.income?.length > 0
      ? state.income
      : [
          {
            type: "State Pension Income",
            start: "",
            amount: {
              value: "",
              unit: "Annual",
            },
          },
        ];

  const [isDialogOpen, setIsDialogOpen] = useState(false);
     const [isCollapsed, setIsCollapsed] = useState(true);

  const {
    updateContextField,
    addItem,
    deleteItem, // optional if needed
  } = useContextUpdater("income");

  const isDisabled = state.income?.length === 0;
  

  return (
    <div className={`card-wrapper ${isCollapsed ? "collapsed" : ""}`}>
      <div className="card-rows card-header">
        <div className="Title">Income</div>
        <div className="card-header-actions">
          <div className="relative-button-wrapper">
            <AddEditButton
              onClick={() => setIsDialogOpen(true)}
              isDisabled={state.details?.length === 0}
            />
            {isDialogOpen && (
              <div className="dialog-overlay">
                <IncomeDialogBox onClose={() => setIsDialogOpen(false)} />
              </div>
            )}
          </div>
          <div
            className={`arrow-toggle ${isCollapsed ? "rotate" : ""}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ArrowDown />
          </div>
        </div>
      </div>
      <div
        className={`card-collapse-container ${isCollapsed ? "collapsed" : ""}`}
      >
        {income.map((income, index) => (
          <div key={index}>
            <div
              className="Title"
              style={{ marginLeft: "var(--Card-Padding)" }}
            >
              {income.type}
              {/* - {income.name} */}
            </div>
            <div className="card-rows">
              <Input1Big
                label="Start Age"
                value={income.start}
                onChange={(val) => updateContextField(index, "start", val)}
                isDisabled={isDisabled}
                readOnly={true}
              />
              <Input1Big
                label="Amount"
                type="number"
                currency={true}
                readOnly
                value={income.amount.value}
                onUnitChange={(unit) =>
                  updateContextField(index, "amount", {
                    ...income.amount,
                    unit,
                  })
                }
                onChange={(val) =>
                  updateContextField(index, "amount", {
                    ...income.amount,
                    value: val,
                  })
                }
                isDisabled={isDisabled}
                options={TimelineOptions}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IncomeCardSmall;
